import React, { useEffect, useState } from "react";
import { useHistory } from "react-router-dom";
import { useSelector, useDispatch } from "react-redux";
import useCrypto from "../../../customHooks/crypto";
import { GoogleMap, useJsApiLoader } from "@react-google-maps/api";

import useLangTranslation from "../../../customHooks/languageProvider";
import moment from "moment";
import CommonStatus from "../Common/CommonStatus";
import useDeviceStatus from "../../../customHooks/deviceStatusProvider";
import { setShowInfo } from "../../AxiosRtk/successMessage";
import KidImage from "../../../shared/KidImage/KidImage";
import { useDashboardDataProvider } from "../../../customHooks/dashboardDataProvider";
import useFirebase from "../../../../useFirebase";

const LocateMyFamily = () => {
  const history = useHistory();
  const dispatch = useDispatch();
  const { decryptRequest } = useCrypto();
  const { kidList, selectedKidId, selectedDeviceData } = useSelector(
    ({ dashboard }) => dashboard
  );
  const MAP_KEY = decryptRequest(process.env.REACT_APP_GOOGLE_MAP_KEY);
  const { getLangTranslation } = useLangTranslation();
  const { handleDeviceSelection } = useDashboardDataProvider();
  const [selectedKid, setSelectedKid] = useState(null);
  const [selectedLocation, setSelectedLocation] = useState();
  const selectedKidData = kidList?.find((item) => item?.ID === selectedKidId);
  const [deviceStatus, setDeviceStatus] = useState("");
  const [selectedDeviceId, setSelectedDeviceId] = useState(null);
  const { getDeviceStatusData } = useDeviceStatus(deviceStatus, setDeviceStatus);
  
  // Store location data for all devices
  const [locationData, setLocationData] = useState({});
  
  const AdminSetRuleLanguageData = useSelector(({ setRules }) => setRules);
  const { getCollectionData } = useFirebase();
  const { appLanguageId } = useSelector(({ misc }) => misc);
  const lang = AdminSetRuleLanguageData?.AdminSetRuleLanguageData?.languages?.find(
    (item) => item?.id === appLanguageId
  );

  useEffect(() => {
    if (selectedDeviceData?.ID) {
      getCollectionData(selectedDeviceData?.ID);
    }

    // Fetch location data for all devices associated with the selected kid
    kidList?.forEach((kid) => {
      kid.KidsDevices?.forEach((device) => {
        getCollectionData(device?.KidsDeviceId, "CurrentLocation")
          .then((res) => {
            setLocationData((prevData) => ({
              ...prevData,
              [device?.KidsDeviceId]: res, // Map device ID to its location data
            }));
          })
          .catch((e) => {
            console.log(e);
          });
      });
    });
  }, [kidList]);

  useEffect(() => {
    if (selectedDeviceData) {
      setSelectedLocation(selectedDeviceData?.ID);
      getDeviceStatusData(selectedDeviceData?.ID);
      setSelectedKid(selectedKidData);
    }
  }, [selectedDeviceData]);

  const handleRadioChange = (device, kidName) => {
    setSelectedDeviceId(device?.KidsDeviceId);
    handleDeviceSelection({ value: device?.KidsDeviceId });
    setSelectedKid(kidName);
    setSelectedLocation(device?.location);
  };

  const { isLoaded } = useJsApiLoader({
    googleMapsApiKey: MAP_KEY,
    libraries: ["places"],
  });

  if (!isLoaded) {
    return "Loading";
  }

  const center = {
    lat: 10.99835602,
    lng: 77.01502627,
  };

  const handleLocateNow = () => {
    if (selectedLocation) {
      history.push({
        pathname: "/locate-now",
        state: {
          data: {
            kidData: selectedKid,
            kidDevice: selectedDeviceData,
            selectedLocation: selectedLocation,
          },
        },
      });
    } else {
      dispatch(
        setShowInfo({
          showInfo: true,
          title: "CHILD_DETAIL_LOCATION",
          defaultTitle: "Location not found",
        })
      );
    }
  };

  const handleDrive = () => {
    if (selectedLocation) {
      history.push({
        pathname: "/drive-to-my-child",
        state: {
          data: {
            kidData: selectedKid,
            kidDevice: selectedDeviceData,
            selectedLocation: selectedLocation,
          },
        },
      });
    } else {
      dispatch(
        setShowInfo({
          showInfo: true,
          title: "CHILD_DETAIL_LOCATION",
          defaultTitle: "Location not found",
        })
      );
    }
  };

  return (
    <>
      <div className="card mb-6">
        <div className="card-body">
          <div className="d-flex align-items-center justify-content-between flex-wrap gap-6">
            <div className="list-item">
              <div className="list-content">
                <div className="d-inline-flex align-items-center lh-1">
                  <div
                    className="text-blue-900 fs-5"
                    onClick={() => history.goBack()}
                  >
                    <i
                      className={`${lang?.isRTL === 1
                        ? "icon-arrow-right"
                        : "icon-arrow-left"
                        }`}
                    />
                  </div>
                  <h3 className="text-blue-900 mb-0">
                    {" "}
                    {getLangTranslation(
                      "ONBOARD_LOCATE_FAMILY",
                      "Locate my family"
                    )}
                  </h3>
                </div>
              </div>
            </div>
            <div
              className="d-inline-flex gap-2 align-items-center pt-cursor"
              onClick={() => history.push("/location-history")}
            >
              <span className="icon-wrap icon-blue flex-shrink-0">
                <i className="icon-history" />
              </span>
              <h6 className="mb-0">
                {getLangTranslation("LOCATION_HISTORY", "Location History")}
              </h6>
            </div>
          </div>
        </div>
      </div>
      <div className="card">
        <div className="card-body">
          <div style={{ width: "100%", height: "51vh" }} className="mb-6">
            <GoogleMap
              center={center}
              zoom={15}
              mapContainerStyle={{ width: "100%", height: "100%" }}
              options={{
                zoomControl: true,
                streetViewControl: true,
                mapTypeControl: true,
                fullscreenControl: true,
              }}
            ></GoogleMap>
          </div>

          <div className="mb-9 d-flex flex-wrap gap-3">
            <div onClick={() => handleLocateNow()} className="btn btn-primary">
              <span className="btn-upper-case">
                {getLangTranslation("CHILD_LOCATION_LOCATE_NOW", "Locate Now")}
              </span>
            </div>
            <div onClick={() => handleDrive()} className="btn btn-primary">
              <span className="btn-upper-case">
                {getLangTranslation(
                  "CHILD_LOCATION_DRIVE_TO_CHILD",
                  "Drive to my child"
                )}
              </span>
            </div>
            <div
              onClick={() =>
                history.push({
                  pathname: "/message-my-child",
                  state: { kidName: selectedKid },
                })
              }
              className="btn btn-outline-primary"
            >
              <span className="btn-upper-case">
                {getLangTranslation(
                  "MESSAGE_TO_MY_CHILD",
                  "Message to my child"
                )}
              </span>
            </div>
          </div>
          {kidList?.map((kid) => (
            <div key={kid?.ID} className="card mb-4 shadow">
              <div className="card-body">
                <div className="d-flex align-items-center gap-2 mb-3">
                  <KidImage imageUrl={kid?.image} gender={kid?.gender} />
                  <div>
                    <h5 className="text-blue-900 mb-0">{kid?.childName}</h5>
                    {kid?.KidsDevices?.find(
                      (device, i) =>
                        device?.KidsDeviceId === selectedDeviceData?.ID
                    ) ? (
                      <CommonStatus status={deviceStatus} />
                    ) : (
                      ""
                    )}
                  </div>
                </div>
                {kid?.KidsDevices?.length > 0 && kid?.KidsDevices[0]?.Name && (
                  <div className="inline-list-wrap separator space-24">
                    {kid?.KidsDevices?.map((device, index) => (
                      <div key={index} className="list-item">
                        <div className="list-content content-336">
                          <div className="form-check mb-3">
                            <input
                              className="form-check-input"
                              type="radio"
                              name="flexRadioDefault"
                              id={`device${device?.KidsDeviceId}`}
                              checked={selectedDeviceId === device?.KidsDeviceId} // Ensure this correctly checks the selected device
                              onChange={() => handleRadioChange(device, kid.childName)} // Use kid.childName instead of kid
                            />
                            <label
                              className="form-check-label"
                              htmlFor={`device${device?.KidsDeviceId}`} // Update this to match the radio button id
                            >
                              {getLangTranslation(
                                "CHILD_LOCATION_DEVICE",
                                "Device:"
                              )}{" "}
                              {device?.Name}
                            </label>
                          </div>
                          <p className="text-dark mb-3">
                            {locationData[device?.KidsDeviceId]
                              ? `${locationData[device?.KidsDeviceId]?.address}`
                              : getLangTranslation(
                                "CHILD_LOCATION_NOT_FOUND",
                                "Child Location not found!"
                              )}
                          </p>
                          <span className="text-small">
                            {device?.location
                              ? moment(device?.location?.locationTime).format(
                                "DD- MMM- YYYY | hh:mm a"
                              )
                              : ""}
                          </span>
                        </div>
                      </div>
                    ))}
                  </div>
                )}

                {(kid?.KidsDevices?.length === 0 ||
                  !kid?.KidsDevices[0]?.Name) && (
                    <div>
                      {getLangTranslation("CHILD_LOCATION_DEVICE", "Device:")}{" "}
                      {getLangTranslation(
                        "LOCATE_FAMILY_KID_DEVICE_NOT_FOUND",
                        "Kid Device location not found"
                      )}
                    </div>
                  )}
              </div>
            </div>
          ))}
        </div>
      </div>
    </>
  );
};

export default LocateMyFamily;
